// The temporary provider login stays in memory. The website's existing account
// is never changed; only explicit confirmation authorizes Windows.
function createDesktopSignInController({ id, request, isCurrent }) {
  if (typeof id !== 'string' || id.length !== 43 || !/^[A-Za-z0-9_-]+$/.test(id)
      || typeof request !== 'function' || typeof isCurrent !== 'function') throw new Error('Open sign-in from the Windows app again.');
  let disposed = false, busy = false, confirmed = false, session = null, ticket = null;
  const revoked = new Set();
  const check = () => { if (disposed || !isCurrent()) throw new Error('This sign-in request changed. Start again in Windows.'); };
  async function revoke(tokens) {
    if (!tokens?.refresh || revoked.has(tokens.refresh)) return;
    revoked.add(tokens.refresh);
    try { await request('/json/auth/logout', { body: { refreshToken: tokens.refresh }, token: tokens.access }); } catch { /* Temporary credentials expire; never retry an approval. */ }
  }
  async function clear() { const previous = session; session = null; await revoke(previous?.tokens); }
  async function run(operation) {
    check();
    if (busy || confirmed) throw new Error('Sign-in is already in progress.');
    busy = true;
    try { return await operation(); } finally { busy = false; }
  }
  async function receive(data) {
    try {
      check();
      if (data?.mfaRequired) {
        if (typeof data.ticket !== 'string' || !data.ticket || data.tokens) throw new Error('The verification session was not confirmed.');
        ticket = data.ticket; return { ticket };
      }
      if (!(data?.user?.id || data?.user?._id) || data.user.flags?.isGhost
          || typeof data.tokens?.access !== 'string' || !data.tokens.access
          || typeof data.tokens.refresh !== 'string' || !data.tokens.refresh
          || !(Date.parse(data.tokens.expiresAt) > Date.now())) throw new Error('The account sign-in was not confirmed.');
      session = { user: data.user, tokens: data.tokens }; ticket = null;
      return { user: data.user };
    } catch (error) { await revoke(data?.tokens); throw error; }
  }
  return {
    identity(input) {
      return run(async () => {
        if (!['google', 'apple'].includes(input?.provider) || typeof input.idToken !== 'string'
            || !input.idToken || input.idToken.length > 16384) throw new Error('Choose Google or Apple to sign in.');
        await clear(); ticket = null; check();
        const body = { idToken: input.idToken, clientInfo: { platform: 'web' },
          ...(input.provider === 'apple' && input.fullName ? { fullName: input.fullName } : {}) };
        return receive(await request(`/json/auth/login/${input.provider}`, { body }));
      });
    },
    completeMfa(data) {
      return run(() => {
        if (!ticket) throw new Error('Start verification again.');
        return receive(data);
      }).catch(async error => { await revoke(data?.tokens); throw error; });
    },
    confirm() {
      return run(async () => {
        if (!session) throw new Error('Sign in with Google or Apple first.');
        confirmed = true;
        try {
          check();
          const result = await request('/json/auth/desktop/approve', { body: { id }, token: session.tokens.access });
          check();
          if (result?.ok !== true) throw new Error('Windows sign-in was not confirmed. Start again in the app.');
          return { success: true };
        } finally { await clear(); }
      });
    },
    dispose() { disposed = true; ticket = null; return clear(); },
  };
}
module.exports = { createDesktopSignInController };
